import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
import type { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import type { Camera } from '@babylonjs/core/Cameras/camera';
// import type { EnvironmentHelper } from '@babylonjs/core/Helpers/environmentHelper';
import { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Observable } from '@babylonjs/core/Misc/observable';
import { CubeTexture } from '@babylonjs/core/Materials/Textures/cubeTexture';
import type { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial';
// import { SSAORenderingPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/ssaoRenderingPipeline';
// import { SSAO2RenderingPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/ssao2RenderingPipeline';
import type { Scene } from '@babylonjs/core/scene';
import { AvatarSex, type ISmplPoseBase } from '@seddi/avatar';
import type { IScene } from '@seddi/scene';
import {
  HiDefRenderingPipeline,
  MeshExt,
  ViewportCore,
  encodeRtoRGBA,
  removeVerticesDataWithNotify,
} from '@seddi/ui-babylon-core';
import { type EntityRenderable, SceneLoader, SceneModule } from '@seddi/ui-babylon-scene';
import { type SmplMeshDesc, buildVertexDataDesc } from '@seddi/ui-babylon-smpl';
import log from 'loglevel';
import { nanoid } from 'nanoid';
import {
  type AvatarTexturesData,
  type AvatarTexturesDesc,
} from './avatars/types/avatarTexturesTypes';
import { CoverageColor, CoverageLevel, SkinTone } from './avatars/types/displayTypes';
import { buildAvatarTextures } from './avatars/avatarTexturesBuilder';
import type { SmplRenderable } from './avatars/smplRenderable';
import { createSmplRenderable, updateSmplRenderableMaterial } from './avatars/smplRenderable';
import { buildHeatMap } from './materials/heatmapBuilder';
import { create as createHeatmapMaterial } from './materials/heatmapMaterial';
import type { FabricPhysicsMaterial } from './simulation/clothElasticEnergy';
// import { ClothElasticEnergy } from './simulation/clothElasticEnergy';
// Extensions
import '@babylonjs/core/Helpers/sceneHelpers';
import '@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent';
import '@babylonjs/core/Rendering/depthRendererSceneComponent';
// import '@babylonjs/core/Rendering/geometryBufferRendererSceneComponent';
// import '@babylonjs/core/Rendering/prePassRendererSceneComponent';
// eslint-disable-next-line import/order -- Resources
import StudioEnvironment from '../assets/environments/studio_softbox_2umbrellas.env';
import { sanitizeGarment } from './garments/sanitizeGarment';
// import MarketEnvironment from '@/public/environments/market.env';

export type UpdateAvatarGeometryPayload = Partial<SmplMeshDesc>;
export type UpdateAvatarTexturesPayload = Partial<AvatarTexturesDesc>;

export interface AvatarGeometryDesc {
  pose: ISmplPoseBase | null;
  sex: AvatarSex | null;
  shape: Float32Array | null;
}

export interface GarmentContext {
  /**
   * Descriptor.
   */
  data: IScene;

  /**
   * Loader.
   */
  loader: SceneLoader;

  /**
   * Child renderables.
   */
  entities: EntityRenderable[];
}

function avatarGeometryDescIsReady(desc: AvatarGeometryDesc): boolean {
  return Boolean(desc.pose) && Boolean(desc.sex) && Boolean(desc.shape);
}

const CAMERA_TARGET = new Vector3(0, 0.9, 0);

// const IS_STRESS_HEATMAP = FlagsModule.isEnabled('stress_maps');
const IS_STRESS_HEATMAP = (() => false)();

export class FitroomViewportContext {
  /**
   * An event triggered after a new garment is ready on the scene.
   */
  public readonly onGarmentReadyObservable = new Observable<FitroomViewportContext>();

  private _core!: ViewportCore;

  private _camera?: Camera;

  private avatar: SmplRenderable | undefined;
  private avatarGeometryDesc: AvatarGeometryDesc;
  private avatarTexturesDesc: AvatarTexturesDesc;
  private garment?: GarmentContext;
  private isAvatarVisible: boolean;

  private _isHeatMap: boolean;
  private heatMaterial!: ShaderMaterial;

  /**
   * Creates a new context.
   */
  constructor() {
    // Avatar geometry descriptor defaults.
    this.avatarGeometryDesc = {
      pose: null,
      sex: null,
      shape: null,
    };

    // Avatar texture descriptor defaults.
    this.avatarTexturesDesc = {
      coverageBottom: CoverageLevel.MODERATE,
      coverageColor: CoverageColor.BLACK,
      coverageTop: CoverageLevel.MODERATE,
      isBottomDisabled: false,
      isTopDisabled: false,
      sex: AvatarSex.Female,
      skin: SkinTone.BROWN_3,
    };

    this.isAvatarVisible = false;

    // Heat map default.
    this._isHeatMap = false;
  }

  /**
   * Gets viewport core.
   */
  get core(): ViewportCore {
    return this._core;
  }

  /**
   * Gets camera.
   */
  get camera(): Camera {
    return this._camera!;
  }

  /**
   * Gets scene.
   */
  get babylonScene(): Scene {
    return this.core.scene;
  }

  /**
   * Gets if heat map is enabled.
   */
  get isHeatMap(): boolean {
    return this._isHeatMap;
  }

  /**
   * Initializes resources attached to life-cycle.
   */
  init(canvas: HTMLCanvasElement): void {
    this._core = new ViewportCore(canvas);
    this.babylonScene.clearColor.set(0.98, 0.98, 0.98, 1);
    this.createSceneDefaults();
    this.initAvatar();
    this.initHeatMap();
    this.createSceneShadows();

    if (IS_STRESS_HEATMAP) {
      this.heatMaterial.wireframe = true;
    }

    // Temp.
    (async () => {
      const texturesData = await buildAvatarTextures(this.avatarTexturesDesc);
      this.updateAvatarMaterial(texturesData);
      this.toggleAvatar(true);
    })().catch(error => {
      log.error(error);
    });
  }

  /**
   * Registers a function to be called after a garment becomes available.
   * @param func - defines the function to register.
   */
  registerGarmentReady(func: (context: FitroomViewportContext) => void): void {
    this.onGarmentReadyObservable.add(func);
  }

  /**
   * Compute garment bounds center.
   */
  getGarmentCenter(): { center: Vector3; radius: number } {
    const { babylonScene } = this;

    const piecesBounds = babylonScene.meshes
      .filter(
        ({ metadata }: { metadata?: { entityType: string } }) =>
          metadata && metadata.entityType === 'hiResGarmentPiece',
      )
      .map(m => m.getBoundingInfo());
    const piecesCenter = piecesBounds.map(b => b.boundingSphere.center);
    if (!piecesCenter.length) {
      return { center: CAMERA_TARGET, radius: 3.04 };
    }
    const numPieces = new Vector3(piecesCenter.length, piecesCenter.length, piecesCenter.length);
    const center = piecesCenter
      .reduce((v, c) => v.addInPlace(c), new Vector3())
      .divideInPlace(numPieces);
    const piecesRadius = piecesBounds.map(b => b.boundingSphere.radius);
    const radius = Math.max(...piecesRadius);

    return { center, radius };
  }

  /**
   * Create garment resources.
   * @param garment - Garment definition.
   */
  async createGarment(data: IScene): Promise<void> {
    log.info('Loading viewport garment');

    this.garment = { data } as GarmentContext;

    // Load garment with scene loader.
    const loader = new SceneLoader(this._core.scene);
    this.garment.loader = loader;
    await loader.loadAsync(data);

    // Gather renderables.
    this.garment.entities = data.children
      .map(e => loader.entities.get(e._id))
      .filter(e => Boolean(e)) as EntityRenderable[];

    // Garment ready.
    this.onGarmentReadyObservable.notifyObservers(this);
  }

  /**
   * Removes the garment from scene.
   */
  async removeGarment(): Promise<void> {
    if (!this.garment) {
      return;
    }

    log.info('Removing viewport garment');

    // Remove garment with scene loader.
    await this.garment.loader.unload(this.garment.data);
    this.garment = undefined;
    this._core.markDirty();
  }

  /**
   * Run render loop.
   */
  runRenderLoop(): void {
    this.core.renderLoopIdleTime = 1000;
    this.core.runRenderLoopIdle();
  }

  /**
   * Enable/Disable avatar visualization.
   */
  toggleAvatar(value: boolean): void {
    if (this.isAvatarVisible === value) {
      return;
    }

    this.isAvatarVisible = value;

    this.avatar!.mesh.setEnabled(value);
    this.core.markDirty();
  }

  /**
   * Enable/Disable heat map visualization.
   */
  toggleHeatMap(value: boolean): void {
    if (this._isHeatMap === value) {
      return;
    }

    this._isHeatMap = value;

    if (!this.garment) {
      return;
    }

    if (value) {
      this.enableHeatMaterial();
    } else {
      this.disableHeatMaterial();
    }
  }

  /**
   * Create avatar mesh.
   * @param geometry - Mesh geometry.
   */
  updateAvatarGeometry(delta: UpdateAvatarGeometryPayload): void {
    // Apply delta updates.
    const desc = this.avatarGeometryDesc;
    let sync = false;

    if (delta.pose && desc.pose !== delta.pose) {
      desc.pose = delta.pose;
      sync = true;
    }
    if (delta.sex && desc.sex !== delta.sex) {
      desc.sex = delta.sex;
      sync = true;
    }
    if (delta.shape && desc.shape !== delta.shape) {
      desc.shape = delta.shape;
      sync = true;
    }

    // Needs update.
    if (!sync) {
      return;
    }

    log.info('Updating viewport avatar');

    // All required information is available.
    const isReady = avatarGeometryDescIsReady(desc);
    if (!isReady) {
      return;
    }

    // Invalidate garment.
    void this.removeGarment();

    // Build avatar mesh and update geometry.
    const data = buildVertexDataDesc(desc as SmplMeshDesc);
    MeshExt.applyGeometry(this.avatar!.mesh, data, true);
    this._core.markDirty();
  }

  /**
   * Update avatar textures.
   * @param delta - Avatar textures delta.
   */
  updateAvatarTextures(delta: UpdateAvatarTexturesPayload): void {
    // Apply delta updates.
    const desc = this.avatarTexturesDesc;
    let sync = false;

    if (delta.coverageBottom && desc.coverageBottom !== delta.coverageBottom) {
      desc.coverageBottom = delta.coverageBottom;
      sync = true;
    }
    if (delta.coverageColor && desc.coverageColor !== delta.coverageColor) {
      desc.coverageColor = delta.coverageColor;
      sync = true;
    }
    if (delta.coverageTop && desc.coverageTop !== delta.coverageTop) {
      desc.coverageTop = delta.coverageTop;
      sync = true;
    }
    if (delta.skin && desc.skin !== delta.skin) {
      desc.skin = delta.skin;
      sync = true;
    }
    if (delta.isBottomDisabled !== undefined && desc.isBottomDisabled !== delta.isBottomDisabled) {
      desc.isBottomDisabled = delta.isBottomDisabled;
      sync = true;
    }
    if (delta.isTopDisabled !== undefined && desc.isTopDisabled !== delta.isTopDisabled) {
      desc.isTopDisabled = delta.isTopDisabled;
      sync = true;
    }
    if (delta.sex && desc.sex !== delta.sex) {
      desc.sex = delta.sex;
      sync = true;
    }

    // Needs update.
    if (!sync) {
      return;
    }

    log.info('Updating viewport avatar textures');

    // Build avatar textures and update material.
    void (async () => {
      const data = await buildAvatarTextures(desc);
      this.updateAvatarMaterial(data);
    })();
  }

  /**
   * Update garment.
   */
  async updateGarment(garment: IScene): Promise<void> {
    sanitizeGarment(garment);
    await this.removeGarment();
    await this.createGarment(garment);
  }

  /**
   * Create avatar mesh and material.
   */
  private initAvatar(): void {
    this.avatar = createSmplRenderable(nanoid(), this.core.scene);
  }

  /**
   * Create heat material.
   */
  private initHeatMap(): void {
    this.heatMaterial = createHeatmapMaterial(this.babylonScene);
    const heatMaterialMax = IS_STRESS_HEATMAP ? 5 : 2;
    this.heatMaterial.setFloat('heatMax', heatMaterialMax);
    this.heatMaterial.setFloat('heatMin', 0);
  }

  /**
   * Create default scene elements.
   */
  private createSceneDefaults(): void {
    const { babylonScene } = this;

    babylonScene.createDefaultCameraOrLight(true, true, true);
    babylonScene.lights[0].dispose();

    // Setup camera.
    const camera = babylonScene.activeCamera as ArcRotateCamera;
    camera.fov = 0.5;
    camera.minZ = 0.01;
    camera.position = new Vector3(0, 1.3, 4);
    camera.target = CAMERA_TARGET;
    camera.useAutoRotationBehavior = false;

    // Setup directional.
    const light1 = new DirectionalLight('light1', new Vector3(-0.2, -1, -0.5), babylonScene);
    light1.intensity = 1.0;
    light1.position = new Vector3(0.2, 1, 0.5);

    // Setup environment.
    const environmentRotation = 1.57;
    const environmentTexture = new CubeTexture(StudioEnvironment, babylonScene);
    environmentTexture.rotationY = environmentRotation;
    babylonScene.environmentTexture = environmentTexture;
    const helper = babylonScene.createDefaultEnvironment({
      backgroundYRotation: environmentRotation,
      cameraExposure: 1.5,
      createSkybox: false,
      enableGroundMirror: false,
      groundShadowLevel: 0.4,
      setupImageProcessing: true,
    })!;
    helper.setMainColor(new Color3(1, 1, 1));

    // Create SSAO and configure.
    const hidefRatio = { combineRatio: 1.0, ssaoRatio: 0.7 };
    const hidef = new HiDefRenderingPipeline('hidef', babylonScene, hidefRatio, []);
    hidef.area = 0.001;
    hidef.base = 0.5;
    hidef.fallOff = 0.000001;
    hidef.radius = 0.00007;
    hidef.totalStrength = 1;

    // const ssaoRatio = { blurRatio: 0.5, ssaoRatio: 0.5 };
    // const ssao = new SSAO2RenderingPipeline('ssao2', babylonScene, ssaoRatio, null, false);
    // ssao.epsilon = 0.005;
    // ssao.expensiveBlur = false;
    // ssao.radius = 0.04;
    // ssao.samples = 10;
    // ssao.totalStrength = 3;
    // babylonScene.prePassRenderer.samples = 4;

    // Attach camera to the SSAO render pipeline.
    babylonScene.postProcessRenderPipelineManager.attachCamerasToRenderPipeline('hidef', camera);

    this._camera = camera;
  }

  /**
   * Create default shadow generator.
   */
  private createSceneShadows(): void {
    const { babylonScene } = this;

    const light = babylonScene.lights[0] as DirectionalLight;
    const generator = new ShadowGenerator(512, light);
    generator.useBlurExponentialShadowMap = true;
    generator.blurKernel = 32;

    for (const mesh of babylonScene.meshes) {
      generator.addShadowCaster(mesh);
    }
  }

  /**
   * Update garment pieces heat material.
   */
  private disableHeatMaterial(): void {
    const garment = this.garment!;

    // Transform piece renderables.
    for (const entity of garment.entities) {
      if (!entity.piece) {
        continue;
      }

      const piece = entity.piece;

      // Remove vertices heat color.
      removeVerticesDataWithNotify(piece.geometry, VertexBuffer.ColorKind);

      // Show fabric materials.
      const fr = piece.fr;
      const bk = piece.bk;
      fr.mesh.material = fr.material;
      bk.mesh.material = bk.material;

      // Show logos.
      const logos = Array.from(piece.logos.values());
      for (const logo of logos) {
        logo.mesh.setEnabled(true);
      }
    }

    this._core.markDirty();
  }

  /**
   * Update garment pieces with heat material.
   */
  private enableHeatMaterial(): void {
    const garment = this.garment!;

    // Fabric physics materials map.
    const data = this.garment!.data;
    const fabricMaterials = new Map<string, FabricPhysicsMaterial>();

    if (IS_STRESS_HEATMAP) {
      for (const fabric of data.fabrics) {
        const { simulationOnline, simulationSurface } = fabric;
        let params = simulationSurface?.params ?? [];
        if (params.length === 0) {
          log.error(`Fabric ${fabric._id} missing simulation parameters`);
          params = simulationOnline?.params ?? [];
        }
        if (params.length === 0) {
          log.error(`Fabric ${fabric._id} missing online simulation parameters`);
        }

        const poissonRatio = params.find(p => p.name === 'kPoisson')?.data ?? 0;
        const stretchBias = params.find(p => p.name === 'kStretchBias')?.data ?? 1;
        const stretchWarp = params.find(p => p.name === 'kStretchWarp')?.data ?? 1;
        const stretchWeft = params.find(p => p.name === 'kStretchWeft')?.data ?? 1;
        const material: FabricPhysicsMaterial = {
          poissonRatioWarp: poissonRatio,
          poissonRatioWeft: poissonRatio,
          stretchBias,
          stretchWarp,
          stretchWeft,
        };
        fabricMaterials.set(fabric.id, material);
      }
    }

    // const pieces = data.children.filter(e => Boolean(e.components.garmentPiece));
    // Process pieces fabric references.
    // const pieceMaterialIds = new Map<string, string>();
    //
    // for (const piece of pieces) {
    //   pieceMaterialIds.set(piece._id, piece.components.garmentPiece!.fabric);
    // }

    const garmentGeometry = SceneModule.sceneExt.garmentGeometry!;

    // Transform piece renderables.
    for (const entity of garment.entities) {
      if (!entity.piece) {
        continue;
      }

      const piece = entity.piece;

      const geometryData = garmentGeometry.pieces.find(p => p.id === entity.id)!;

      // Build vertex heat values.
      let heats!: Float32Array;
      if (IS_STRESS_HEATMAP) {
        // const material = fabricMaterials.get(materialId)!;
        // heats = ClothElasticEnergy.build(
        //   geometryData.indices,
        //   geometryData.position3D,
        //   geometryData.position2D,
        //   material,
        // );
      } else {
        heats = buildHeatMap(
          geometryData.indices,
          geometryData.position3D,
          geometryData.position2D,
        );
      }

      const geometry = piece.geometry;

      if (IS_STRESS_HEATMAP) {
        // const geometryCopy = geometry.copy(`${piece.id}-GEOMETRY-H`);
        // fr = new Mesh(`${piece.id}-MESH-H`, this.babylonScene);
        // geometryCopy.applyToMesh(fr);
      }

      // Modify geometry inject heats as colors.
      const colors = encodeRtoRGBA(heats);
      geometry.setVerticesData(VertexBuffer.ColorKind, colors);

      // Assign heat material.
      const fr = piece.fr;
      const bk = piece.bk;
      fr.mesh.material = this.heatMaterial;
      bk.mesh.material = this.heatMaterial;

      // Hide logos.
      const logos = Array.from(piece.logos.values());
      for (const logo of logos) {
        logo.mesh.setEnabled(false);
      }
    }

    this._core.markDirty();
  }

  /**
   * Update avatar material.
   * @param data - Avatar textures data.
   */
  private updateAvatarMaterial(data: AvatarTexturesData): void {
    log.info('Updating viewport avatar material');

    updateSmplRenderableMaterial(this.avatar!.material, data);
    this.core.markDirty();
  }
}
